import {useState, createContext, useContext, useMemo, Dispatch, SetStateAction, FC} from 'react'
import {ID, WithChildren} from '../../../_metronic/helpers'

type PaginationProps = {
  perPage: number
  currentPage: number
  totalResults: number
}

export type KTableContextProps = {
  selected: Array<ID>
  selectedLav: Array<ID>
  selectedOn: Array<ID>
  onSelect: (selectedId: ID) => void
  onSelectLav: (selectedId: ID) => void
  onlySelect: (selectedIdOn: ID) => void
  onSelectAll: () => void
  onSelectAllLav: () => void
  clearSelected: () => void
  data: any[]
  setData: Dispatch<SetStateAction<any>>
  pagination: PaginationProps
  setPagination: Dispatch<SetStateAction<PaginationProps>>
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  updateTable: boolean
  setUpdateTable: Dispatch<SetStateAction<boolean>>
  clearTable: boolean
  setClearTable: Dispatch<SetStateAction<boolean>>
  updateTableDetail: boolean
  setUpdateTableDetail: Dispatch<SetStateAction<boolean>>
  clearTableDetail: boolean
  setClearTableDetail: Dispatch<SetStateAction<boolean>>
  // NULL => (CREATION MODE) | MODAL IS OPENED
  // NUMBER => (EDIT MODE) | MODAL IS OPENED
  // UNDEFINED => MODAL IS CLOSED
  itemIdForUpdate?: ID
  setItemIdForUpdate: Dispatch<SetStateAction<ID>>
  isAllSelected: boolean
  isAllSelectedLav: boolean
  disabled: boolean
}

export const initialKTable: KTableContextProps = {
  selected: [],
  selectedLav: [],
  selectedOn: [],
  onlySelect: () => {},
  onSelect: () => {},
  onSelectLav: () => {},
  onSelectAll: () => {},
  onSelectAllLav: () => {},
  clearSelected: () => {},
  setItemIdForUpdate: () => {},
  isAllSelected: false,
  isAllSelectedLav: false,
  disabled: false,
  data: [],
  setData: () => {},
  loading: false,
  setLoading: () => {},
  updateTable: false,
  setUpdateTable: () => {},
  clearTable: false,
  setClearTable: () => {},
  updateTableDetail: false,
  setUpdateTableDetail: () => {},
  clearTableDetail: false,
  setClearTableDetail: () => {},
  pagination: {
    currentPage: 1,
    perPage: 10,
    totalResults: 0,
  },
  setPagination: () => {},
}

const KTableContext = createContext<KTableContextProps>(initialKTable)

// type Props<T> = {
//   data: T[]
// }

const KTableProvider: FC<WithChildren> = ({children}) => {
  const [selected, setSelected] = useState<Array<ID>>(initialKTable.selected)
  const [selectedLav, setSelectedLav] = useState<Array<ID>>(initialKTable.selected)

  const [selectedOn, setSelectedOnly] = useState<Array<ID>>(initialKTable.selectedOn)
  const [loading, setLoading] = useState(initialKTable.loading)
  const [updateTable, setUpdateTable] = useState(initialKTable.updateTable)
  const [clearTable, setClearTable] = useState(initialKTable.clearTable)
  const [updateTableDetail, setUpdateTableDetail] = useState(initialKTable.updateTableDetail)
  const [clearTableDetail, setClearTableDetail] = useState(initialKTable.clearTableDetail)

  const [pagination, setPagination] = useState(initialKTable.pagination)
  const [itemIdForUpdate, setItemIdForUpdate] = useState<ID>(initialKTable.itemIdForUpdate)
  const disabled = false
  const [data, setData] = useState([])

  // Resto del código del proveedor...

  //   const {isLoading} = useQueryResponse()
  // const disabled = useMemo(() => calculatedGroupingIsDisabled(isLoading, data), [isLoading, data])

  const isAllSelected = useMemo(() => calculateIsAllDataSelected(data, selected), [data, selected])
  const isAllSelectedLav = useMemo(
    () => calculateIsAllDataSelectedLav(data, selectedLav),
    [data, selectedLav],
  )

  function groupingOnSelect(id: ID, setSelected: Dispatch<SetStateAction<Array<ID>>>) {
    if (id === null || id === undefined) {
      return
    }
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id)
      }
      return [...prev, id]
    })
  }
  function groupingOnSelectLav(
    id: ID,
    selectedLav: Array<ID>,
    setSelectedLav: Dispatch<SetStateAction<Array<ID>>>,
  ) {
    if (id === null || id === undefined) {
      return
    }

    if (selectedLav.includes(id)) {
      setSelectedLav(selectedLav.filter((itemId) => itemId !== id))
    } else {
      const updatedSelected = [...selectedLav]
      updatedSelected.push(id)
      setSelectedLav(updatedSelected)
    }
  }
  function OnlySelect(
    id: ID,
    selectedOn: Array<ID>,
    setSelectedOnly: Dispatch<SetStateAction<Array<ID>>>,
  ) {
    if (id === null || id === undefined) {
      return
    }
    if (selectedOn.includes(id)) {
      setSelectedOnly(selectedOn.filter((itemId) => itemId !== id))
    } else {
      setSelectedOnly([id])
    }
  }

  function groupingOnSelectAll<T>(
    isAllSelected: boolean,
    setSelected: Dispatch<SetStateAction<Array<ID>>>,
    data?: Array<T & {Id?: ID; columna?: ID; ReservaCamaId?: ID}>,
  ) {
    if (isAllSelected) {
      setSelected([])
      return
    }

    if (!data || !data.length) {
      return
    }
    setSelected(
      data
        .filter((item) => item.Id || item.columna || item.ReservaCamaId)
        .map((item) => item.Id || item.columna || item.ReservaCamaId),
    )
  }
  function groupingOnSelectAllLav<T>(
    isAllSelectedLav: boolean,
    setSelectedLav: Dispatch<SetStateAction<Array<ID>>>,
    data?: Array<T & {id?: ID}>,
  ) {
    if (isAllSelectedLav) {
      setSelectedLav([])
      return
    }

    if (!data || !data.length) {
      return
    }
    const updatedSelected = [...selectedLav]
    let getArrayId = data.filter((item) => item.id).map((item) => item.id)

    setSelectedLav([...updatedSelected, ...getArrayId])
  }
  function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
    if (!data) {
      return false
    }

    return data.length > 0 && data.length === selected.length
  }
  function calculateIsAllDataSelectedLav<T>(
    data: Array<T> | undefined,
    selected: Array<ID>,
  ): boolean {
    if (!data) {
      return false
    }

    const allExists = data.every((obj: any) => selected.includes(obj.id))
    return data.length > 0 && allExists
  }

  return (
    <KTableContext.Provider
      value={{
        selected,
        selectedLav,
        selectedOn,
        itemIdForUpdate,
        setItemIdForUpdate,
        disabled,
        isAllSelected,
        isAllSelectedLav,
        data,
        setData,
        pagination,
        setPagination,
        loading,
        setLoading,
        updateTable,
        setUpdateTable,
        clearTable,
        setClearTable,
        updateTableDetail,
        setUpdateTableDetail,
        clearTableDetail,
        setClearTableDetail,
        onlySelect: (id: ID) => {
          OnlySelect(id, selectedOn, setSelectedOnly)
        },
        onSelect: (id: ID) => {
          groupingOnSelect(id, setSelected)
        },
        onSelectLav: (id: ID) => {
          groupingOnSelectLav(id, selectedLav, setSelectedLav)
        },
        onSelectAll: () => {
          groupingOnSelectAll(isAllSelected, setSelected, data)
        },
        onSelectAllLav: () => {
          groupingOnSelectAllLav(isAllSelectedLav, setSelectedLav, data)
        },
        clearSelected: () => {
          setSelected([])
          setSelectedOnly([])
          setSelectedLav([])
        },
      }}
    >
      {children}
    </KTableContext.Provider>
  )
}

const useKTable = () => useContext(KTableContext)

export {KTableProvider, useKTable}
